<template>
  <div>
    <component :is="formComponent" v-if="formComponent" :key="`${careType}-${section}`" />
    <generic-error v-else button-value="Back to profile" :button-to="profileUrl" />
  </div>
</template>

<script lang="ts">
import { careTypeRouteToGraphqlKey, isChildCareEditRoute } from './lib/careTypeUtils';
// General components
import GenericError from '@/components/generic_error/GenericError.vue';
import BioForm from './forms/general/BioForm.vue';
import EducationForm from './forms/general/EducationForm.vue';
import SpecialtySkillsForm from './forms/general/SpecialtySkillsForm.vue';
import CertificationsForm from './forms/general/CertificationsForm.vue';

// Housekeeping specific
import HousekeepingRate from './forms/housekeeping/RateForm.vue';
import HousekeepingServices from './forms/housekeeping/ServicesForm.vue';
import HousekeepingExperience from './forms/housekeeping/ExperienceForm.vue';

// Pet care specific
import PetCareExperience from './forms/pet_care/ExperienceForm.vue';
import PetCareRate from './forms/pet_care/RateForm.vue';
import PetCareServices from './forms/pet_care/ServicesForm.vue';

// Companion care specific
import CompanionCareRate from './forms/companionCare/RateForm.vue';
import CompanionCareServices from './forms/companionCare/ServicesForm.vue';
import CompanionCareExperience from './forms/companionCare/ExperienceForm.vue';
import CompanionCareSpecialtySkills from './forms/companionCare/SpecialtySkillsForm.vue';

type SectionRegistry = Record<string, any>;

const FORM_REGISTRY: Record<string, SectionRegistry> = {
  housekeeping: {
    bio: BioForm,
    rates: HousekeepingRate,
    services: HousekeepingServices,
    experience: HousekeepingExperience,
    education: EducationForm,
    'specialty-skills': SpecialtySkillsForm,
  },
  petCare: {
    bio: BioForm,
    education: EducationForm,
    experience: PetCareExperience,
    'specialty-skills': SpecialtySkillsForm,
    rates: PetCareRate,
    services: PetCareServices,
  },
  companionCare: {
    bio: BioForm,
    rates: CompanionCareRate,
    services: CompanionCareServices,
    'additional-services': CompanionCareServices,
    experience: CompanionCareExperience,
    education: EducationForm,
    'specialty-skills': CompanionCareSpecialtySkills,
    certifications: CertificationsForm,
  },
};

export default {
  name: 'ProfileEditFormDispatcher',
  components: {
    GenericError,
    CompanionCareSpecialtySkills,
  },
  props: {
    section: {
      type: String,
      required: true,
    },
  },
  computed: {
    careType(): string {
      const param = this.$route.params.care_type;
      return Array.isArray(param) ? param[0] : param;
    },
    resolvedSection(): string {
      if (this.section) return this.section;
      const leaf = this.$route.path.split('/').filter(Boolean).pop();
      return leaf || '';
    },
    formComponent(): any {
      if (this.careType === 'companion_care' && this.resolvedSection === 'specialty-skills') {
        return CompanionCareSpecialtySkills;
      }

      const group = isChildCareEditRoute(this.careType)
        ? 'childCare'
        : careTypeRouteToGraphqlKey[this.careType as keyof typeof careTypeRouteToGraphqlKey];
      return FORM_REGISTRY[group]?.[this.resolvedSection] || null;
    },
    profileUrl(): string {
      const sitterUuid = this.$store.getters['account/getAccount']?.id;
      return `/provider/profile/${this.careType}/${sitterUuid}`;
    },
  },
};
</script>
